<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomMoney, AtomButton, AtomText } from "@chatfood/design-system";
import { computed } from "vue";
import { useModalHubStore } from "../modal-hub";
import { useLocale } from "~/composables/use-locale";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";
import { useCustomerStore } from "~/stores/customer";
import { useBusinessStore } from "~/stores/business";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useWindowResize } from "~/composables/use-window-resize";
import { I18nTranslate } from "~/components/i18n-translate";

const css = bemBuilder("loyalty-nudger");

const { t } = useLocale();
const { isMobile } = useWindowResize();

const modalHub = useModalHubStore();
const business = useBusinessStore();
const customer = useCustomerStore();
const loyaltyProgram = useLoyaltyProgramStore();
const featureMatrix = useFeatureMatrixStore();

// TODO: replace to customer loyalty wallet cash balance instead
const customerLoyaltyBalance = computed(() => customer.loyalty.balance);
</script>

<template>
  <div
    :class="{
      [css()]: true,
      [css('', 'user')]: customer.isLogged,
      [css('', 'guest')]: !customer.isLogged,
    }"
  >
    <div
      v-if="customer.isLogged && customerLoyaltyBalance === 0"
      data-test="loyalty-without-balance"
    >
      <AtomText :type="isMobile ? 'body-bold' : 'subtitle'" tag="div">
        {{ t("components.loyalty_nudger.user_title_without_credit") }}
      </AtomText>
      <AtomText tag="div" :type="isMobile ? 'small' : 'body'">
        {{
          t("components.loyalty_nudger.user_subtitle_without_credit", {
            percentage: loyaltyProgram.orderAmountPercentageWorth,
          })
        }}
      </AtomText>
    </div>
    <div
      v-else-if="customer.isLogged && customerLoyaltyBalance > 0"
      data-test="loyalty-with-balance"
    >
      <AtomText :type="isMobile ? 'body-bold' : 'subtitle'" tag="div">
        {{ t("components.loyalty_nudger.user_title") }}
      </AtomText>
      <AtomText tag="div" :type="isMobile ? 'small' : 'body'">
        <I18nTranslate keypath="components.loyalty_nudger.user_subtitle">
          <template #amount>
            <AtomText :type="isMobile ? 'small-bold' : 'body-bold'">
              <AtomMoney
                :amount="customerLoyaltyBalance"
                :currency="business.currency"
              />
            </AtomText>
          </template>
        </I18nTranslate>
      </AtomText>
    </div>
    <template v-else>
      <div data-test="loyalty-guest">
        <AtomText :type="isMobile ? 'small' : 'body'" tag="div">
          {{ t("components.loyalty_nudger.guest_title") }}
        </AtomText>
        <AtomText tag="div" :type="isMobile ? 'body-bold' : 'subtitle'">
          {{
            t("components.loyalty_nudger.guest_subtitle", {
              percentage: loyaltyProgram.orderAmountPercentageWorth,
            })
          }}
        </AtomText>
      </div>
      <AtomButton
        v-if="!featureMatrix.skipAuthFlow"
        :wide="isMobile"
        @click="() => modalHub.showAuthManager()"
      >
        <template v-if="loyaltyProgram.signupPoints > 0">
          <I18nTranslate keypath="components.loyalty_nudger.guest_cta_signup">
            <template #amount>
              <AtomMoney
                :amount="loyaltyProgram.signupPoints"
                :currency="business.currency"
                hide-cents
              />
            </template>
          </I18nTranslate>
        </template>
        <template v-else>
          {{ t("components.loyalty_nudger.guest_cta") }}
        </template>
      </AtomButton>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-nudger {
  display: flex;
  padding: var(--spacing-md);
  background: var(--color-base-95);
  width: 100%;
  border-radius: var(--radii-card-sm);
  line-height: var(--line-height-regular);
  flex-direction: column;
  justify-content: center;

  &--guest {
    gap: var(--spacing-sm);
    justify-content: space-between;

    @media (width > $breakpoint-sm) {
      flex-direction: row;
      align-items: center;
    }
  }
}
</style>
